import React, { useState, useEffect } from "react";
import axios from "axios";
import { Nav, SpineRail, Footer } from "../components/site/Chrome";
import { Reveal, Eyebrow } from "../components/site/primitives";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Minimize2, Clock, AlertTriangle, CheckCircle2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  "name": "MIS TLIF (Minimally Invasive Transforaminal Lumbar Interbody Fusion)",
  "alternateName": "MIS TLIF",
  "description": "MIS TLIF achieves the same fusion as traditional open TLIF but reaches the spine through a small incision using a tubular retractor that dilates the muscle instead of cutting it, generally allowing a faster, less painful recovery.",
  "bodyLocation": "Lumbar spine",
  "performedBy": { "@type": "Physician", "name": "Dr. Amit Kumar Chanduka", "medicalSpecialty": "https://schema.org/Neurosurgery" },
  "areaServed": { "@type": "City", "name": "Bhubaneswar" }
};

const REASONS = [
  "Single or two-level disc disease causing nerve compression and leg pain",
  "Spondylolisthesis (a slipped vertebra) where the instability is confined to one level",
  "Spinal stenosis with instability, where decompression and fusion are both needed",
  "Patients who are otherwise good surgical candidates and want the fastest reasonable recovery",
  "Cases with minimal deformity, prior spine surgery, or complex anatomy at the affected level are usually better suited to open surgery instead",
];
const STEPS = [
  "You lie face-down on the operating table.",
  "Using X-ray guidance, Dr. Chanduka places a small tubular retractor through a 2\u20132.5-centimetre incision, dilating the muscle rather than cutting it.",
  "Working through the tube, the damaged disc is removed and the compressed nerve is decompressed.",
  "A spacer (cage) packed with bone graft is placed in the disc space to restore height and support.",
  "Screws and rods are placed through small separate incisions, guided by X-ray, to stabilise the segment.",
  "The tube is removed at the end, leaving only one or two small incisions rather than one long one.",
];
const BEFORE = [
  "Blood tests, and an ECG or chest X-ray if advised, to confirm you are fit for anaesthesia",
  "Some regular medicines (especially blood thinners) may need to be paused — you will get exact instructions",
  "You will be asked to stop eating and drinking for a set number of hours before surgery",
  "Arrange for a family member to be with you, and for help at home for the first 1\u20132 weeks after you return",
];
const RECOVERY = [
  ["Day 0\u20132", "Many patients are walking with support within hours of surgery. Pain is typically mild and well controlled with medication. Hospital stay is often just 2\u20133 days."],
  ["Week 1\u20132", "Rest at home with regular short walks. Avoid bending, twisting, or lifting anything heavier than about 2\u20133 kg."],
  ["Week 3\u20134", "Walking distance increases steadily. Light household and desk-based activity often resumes during this window."],
  ["Month 2", "Back strengthening exercises typically begin to rebuild core and back strength."],
  ["Month 3\u201312", "Bone fusion continues to strengthen, checked with follow-up X-rays. Full activity is guided by how solid the fusion looks and how you feel."],
];
const REDFLAGS = [
  "Fever, or redness/discharge from any incision",
  "New or worsening numbness, weakness, or pain in the legs",
  "Difficulty controlling your bladder or bowel",
  "Severe pain not relieved by your prescribed medication",
];

const SectionTitle = ({ children }) => <h2 className="font-display font-light text-3xl md:text-4xl text-[#161C22]">{children}</h2>;
const BulletList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-[#161C22]/75 leading-relaxed">
        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[#5ECCA0] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
const NumberedList = ({ items }) => (
  <ol className="space-y-4">
    {items.map((item, i) => (
      <li key={i} className="flex gap-4 text-[#161C22]/75 leading-relaxed">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#E3F0EA] text-[#1F3864] text-sm font-medium flex items-center justify-center font-mono-ui">{i + 1}</span>
        <span className="pt-0.5">{item}</span>
      </li>
    ))}
  </ol>
);
const Callout = ({ title, children }) => (
  <div className="rounded-xl border p-6 bg-[#E3F0EA] border-[#5ECCA0]/40">
    <div className="text-sm font-medium mb-1.5 text-[#1F3864]">{title}</div>
    <p className="text-sm leading-relaxed text-[#161C22]/70">{children}</p>
  </div>
);

export default function MISTLIF() {
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "MIS TLIF Surgery in Bhubaneswar — Dr. Amit Chanduka";
    return () => { document.title = prevTitle; };
  }, []);
  useEffect(() => {
    axios.get(`${API}/specialization-faqs`, { params: { slug: "mis-tlif" } }).then((res) => setFaqs(res.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#F3F5F4] min-h-screen" data-testid="mis-tlif-page">
      <script type="application/ld+json">{JSON.stringify(SCHEMA)}</script>
      <SpineRail />
      <Nav />
      <main>
        <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-[#1F3864] text-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <a href="/#specializations" className="inline-flex items-center gap-2 text-white/60 hover:text-[#5ECCA0] text-sm mb-8 transition-colors">
                <ArrowLeft size={16} /> Back to Specializations
              </a>
              <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center text-[#5ECCA0] mb-6">
                <Minimize2 size={26} strokeWidth={1.5} />
              </div>
              <Eyebrow tone="onDark">Specialization</Eyebrow>
              <h1 className="font-display font-light text-5xl md:text-6xl mt-5 leading-tight">MIS TLIF</h1>
              <p className="text-white/70 mt-3 text-lg md:text-xl">Minimally Invasive Transforaminal Lumbar Interbody Fusion — a guide for patients and families</p>
              <p className="text-white/60 mt-6 max-w-xl leading-relaxed">
                A guide by Dr. Amit Chanduka, Neurosurgeon &amp; Spine Surgeon, explaining MIS TLIF, who it suits, and
                what to expect before, during, and after surgery.
              </p>
              <a href="/#contact" className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What Is MIS TLIF?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                MIS TLIF stands for Minimally Invasive Transforaminal Lumbar Interbody Fusion. It achieves exactly
                the same goal as traditional (open) TLIF — removing a damaged disc and fusing two vertebrae into one
                stable segment — but reaches the spine through a small incision instead of a single long midline cut.
              </p>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Dr. Chanduka uses a tubular retractor system that gently spreads the muscle fibres apart rather than
                cutting or peeling them off the bone. This means the same surgery is performed with far less
                disruption to the surrounding muscle, which is the main reason recovery tends to be quicker and less
                painful than with the open approach.
              </p>
              <div className="mt-6">
                <Callout title="A gentler route to the same result">
                  Think of it as reaching the same destination through a narrow, dilated tunnel instead of a
                  wide-open path — the surgery itself is just as thorough, but the route there is gentler on the
                  body.
                </Callout>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>When Is MIS TLIF Recommended?</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                MIS TLIF is Dr. Chanduka's preferred approach for most straightforward single or two-level fusions,
                where the anatomy doesn't demand the wider access of open surgery. It is typically considered for:
              </p>
              <div className="mt-6"><BulletList items={REASONS} /></div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>What Happens During the Procedure</SectionTitle>
              <div className="mt-6"><NumberedList items={STEPS} /></div>
              <div className="flex items-center gap-2.5 mt-8 text-[#161C22]/60 text-sm">
                <Clock size={16} className="text-[#5ECCA0]" />
                The surgery usually takes between one and a half and two and a half hours, for a single level.
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Preparing for Your Surgery</SectionTitle>
              <div className="mt-6"><BulletList items={BEFORE} /></div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Recovery: What to Expect</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                Because the muscle is dilated rather than cut, recovery is generally faster and less painful than
                open surgery.
              </p>
              <div className="mt-6 space-y-4">
                {RECOVERY.map(([stage, text]) => (
                  <div key={stage} className="bg-white rounded-xl border border-[#DCE3E0] p-5">
                    <div className="font-medium text-[#1F3864] text-sm mb-1">{stage}</div>
                    <p className="text-sm text-[#161C22]/70 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-[900px] mx-auto px-5 md:px-8">
            <Reveal>
              <SectionTitle>Risks and When to Call Us</SectionTitle>
              <p className="text-[#161C22]/75 mt-5 leading-relaxed">
                MIS TLIF carries the same general risks as any spine fusion — infection, bleeding, nerve irritation,
                or the fusion taking longer than expected to solidify — but typically with less blood loss and less
                post-operative muscle pain than the open approach.
              </p>
              <p className="text-[#161C22] font-medium mt-8 mb-4">Please contact the clinic promptly if, after surgery, you notice:</p>
              <div className="rounded-xl border border-red-200 bg-red-50 p-6">
                <ul className="space-y-3">
                  {REDFLAGS.map((item, i) => (
                    <li key={i} className="flex gap-3 text-red-700/90 leading-relaxed text-sm">
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </section>

        {faqs.length > 0 && (
          <section className="py-16 md:py-20">
            <div className="max-w-[900px] mx-auto px-5 md:px-8">
              <Reveal>
                <SectionTitle>Frequently Asked Questions</SectionTitle>
                <Accordion type="single" collapsible className="mt-8">
                  {faqs.map((item) => (
                    <AccordionItem key={item.id} value={item.id} className="border-[#DCE3E0]">
                      <AccordionTrigger className="text-left font-display text-lg text-[#161C22] hover:no-underline">{item.question}</AccordionTrigger>
                      <AccordionContent className="text-[#161C22]/70 leading-relaxed">{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Reveal>
            </div>
          </section>
        )}

        <section className="py-16 md:py-20 bg-[#1F3864] text-white text-center">
          <div className="max-w-[700px] mx-auto px-5 md:px-8">
            <Reveal>
              <CheckCircle2 size={28} className="text-[#5ECCA0] mx-auto mb-5" />
              <h2 className="font-display font-light text-3xl md:text-4xl">Have questions about MIS TLIF?</h2>
              <p className="text-white/70 mt-4 leading-relaxed">
                This page is for general education and does not replace a personal consultation. Please discuss
                how this applies to your own case with Dr. Chanduka before making any treatment decision.
              </p>
              <a href="/#contact" className="inline-flex items-center rounded-full text-sm px-6 py-3 mt-8 bg-[#5ECCA0] text-[#14263D] hover-lift hover:shadow-lg hover:shadow-[#5ECCA0]/25 transition-colors">
                Book Consultation
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
